import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import { useTeamStore } from '@/stores/team'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import { useHasInternalAdminRole } from '@/hooks/useTrialStatus'
import { GET_ALL_TEAMS } from '@/graphql/queries/team'
import { apolloClient } from '@/lib/apollo'
import { markTeamSwitchedForChargebee } from '@/lib/chargebee'
import { classNames } from '@/lib/utils'
import { AddAccountModal } from '@/components/AddAccountModal'
import type { Team } from '@/types'

type LevelFilter = 'all' | 'top-level' | 'sub-team'
type AdminFilter = 'all' | 'mine'

const PAGE_SIZE = 100
const SEARCH_KEY = 'rolla_brand_switcher_search'

export function BrandSwitcher() {
  const { brandSwitcherOpen, closeBrandSwitcher } = useUIStore()
  const allTeamsFromStore = useTeamStore((s) => s.allTeams)
  const selectedTeamId = useAuthStore((s) => s.selectedTeamId)
  const isGlobalAdmin = useAuthStore((s) => s.isGlobalAdmin)
  const setSelectedTeamId = useAuthStore((s) => s.setSelectedTeamId)
  const teamAdmins = useAuthStore((s) => s.teamAdmins)
  const hasInternalAdminRole = useHasInternalAdminRole()
  const [search, setSearch] = useState(() => localStorage.getItem(SEARCH_KEY) ?? '')
  const [debouncedSearch, setDebouncedSearch] = useState(() => localStorage.getItem(SEARCH_KEY)?.trim() ?? '')
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all')
  const [adminFilter, setAdminFilter] = useState<AdminFilter>('all')
  const myTeamIds = new Set(teamAdmins.map((ta) => ta.teamId))
  const [addAccountOpen, setAddAccountOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Debounce search input & persist
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim())
      if (search.trim()) localStorage.setItem(SEARCH_KEY, search.trim())
      else localStorage.removeItem(SEARCH_KEY)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Server-side query for globaladmins
  const { data, loading, fetchMore } = useQuery(GET_ALL_TEAMS, {
    variables: { searchStr: debouncedSearch || undefined, limit: PAGE_SIZE, offset: 0 },
    skip: !brandSwitcherOpen || !isGlobalAdmin,
    fetchPolicy: 'cache-and-network',
  })

  // The InMemoryCache has no merge/keyArgs policy for getTeams, so cache-and-network's periodic
  // network re-validation can race with fetchMore's manual row-concatenation (updateQuery below)
  // and leave the same team appearing twice in `rows` — dedupe defensively by id rather than
  // relying on exactly-once semantics the cache doesn't actually provide here.
  const rawServerTeams = data?.getTeams?.rows ?? []
  const seenServerTeamIds = new Set<string>()
  const serverTeams = rawServerTeams.filter((t: { id: string }) => {
    if (seenServerTeamIds.has(t.id)) return false
    seenServerTeamIds.add(t.id)
    return true
  })
  const serverCount = data?.getTeams?.count ?? 0

  // Map server teams to the Team shape + extra display fields
  const mappedServerTeams: (Team & {
    planName?: string | null
    createdAt?: string | null
    maxSeats?: number | null
  })[] = serverTeams.map(
    (t: {
      id: string
      name: string
      teamLogoURL: string | null
      tags: string[]
      slug: string | null
      customPushNotificationAccess: boolean
      teamParentId: string | null
      planName?: string | null
      createdAt?: string | null
      maxActiveTemplates?: number | null
      maxSeats?: number | null
    }) => ({
      id: t.id,
      name: t.name,
      logoURL: t.teamLogoURL,
      tags: t.tags ?? [],
      slug: t.slug,
      customPushNotificationAccess: t.customPushNotificationAccess,
      maxActiveTemplates: t.maxActiveTemplates ?? null,
      parentTeamId: t.teamParentId,
      planName: t.planName,
      createdAt: t.createdAt,
      maxSeats: t.maxSeats,
    }),
  )

  // For non-globaladmins, use store teams; for globaladmins, use server teams
  const baseTeams = isGlobalAdmin ? mappedServerTeams : allTeamsFromStore

  // Client-side level filter
  const filteredTeams = (() => {
    let teams = baseTeams
    if (levelFilter === 'top-level') teams = teams.filter((t) => !t.parentTeamId)
    else if (levelFilter === 'sub-team') teams = teams.filter((t) => !!t.parentTeamId)

    // Only meaningful for global admins — a non-global-admin's team list is already scoped to
    // teams they admin, so "mine" vs "all" would be a no-op for them.
    if (isGlobalAdmin && adminFilter === 'mine') teams = teams.filter((t) => myTeamIds.has(t.id))

    // For non-globaladmins, also do client-side text search
    if (!isGlobalAdmin && search.trim()) {
      const q = search.toLowerCase()
      teams = teams.filter((t) => t.name.toLowerCase().includes(q) || t.id.toLowerCase().includes(q))
    }

    return teams
  })()

  const hasMore = isGlobalAdmin && mappedServerTeams.length < serverCount

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return
    fetchMore({
      variables: { offset: serverTeams.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return {
          getTeams: {
            ...fetchMoreResult.getTeams,
            rows: [...(prev.getTeams?.rows ?? []), ...fetchMoreResult.getTeams.rows],
          },
        }
      },
    })
  }, [hasMore, loading, fetchMore, serverTeams.length])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || !hasMore || loading) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      loadMore()
    }
  }, [hasMore, loading, loadMore])

  if (!brandSwitcherOpen) return null

  const handleSelect = (teamId: string) => {
    if (teamId !== selectedTeamId) {
      setSelectedTeamId(teamId)
      // Clear Apollo cache so all queries refetch for the new team
      apolloClient.resetStore()
      // Chargebee.js has no supported way to clear its own internal per-customer state without
      // a real page reload — mark it here, SettingsPage forces that reload on mount if needed.
      markTeamSwitchedForChargebee()
    }
    closeBrandSwitcher()
  }

  const displayCount = isGlobalAdmin ? serverCount : allTeamsFromStore.length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeBrandSwitcher}>
      <div
        className="mx-4 max-h-[80vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Switch Account</h2>
          <p className="mt-1 text-sm text-gray-500">
            {isGlobalAdmin ? `${filteredTeams.length} of ${displayCount} teams` : 'Select a team to manage'}
          </p>
        </div>

        {(displayCount > 5 || isGlobalAdmin) && (
          <div className="border-b border-gray-200 px-4 py-2 space-y-2">
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              autoFocus
            />
            {isGlobalAdmin && (
              <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                {(['all', 'top-level', 'sub-team'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setLevelFilter(tab)}
                    className={classNames(
                      'flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                      levelFilter === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                    )}
                  >
                    {tab === 'all' ? 'All' : tab === 'top-level' ? 'Top Level' : 'Sub-teams'}
                  </button>
                ))}
              </div>
            )}
            {isGlobalAdmin && (
              <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                {(['all', 'mine'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setAdminFilter(tab)}
                    className={classNames(
                      'flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                      adminFilter === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                    )}
                  >
                    {tab === 'all' ? 'All Teams' : 'My Teams'}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div ref={scrollRef} className="max-h-96 overflow-y-auto p-2 space-y-1" onScroll={handleScroll}>
          {filteredTeams.map((team) => (
            <button
              key={team.id}
              onClick={() => handleSelect(team.id)}
              className={classNames(
                'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors',
                team.id === selectedTeamId ? 'bg-primary-50 ring-1 ring-primary-200' : 'hover:bg-gray-50',
              )}
            >
              {team.logoURL ? (
                <img src={team.logoURL} alt={team.name} className="h-10 w-10 flex-shrink-0 rounded-lg object-cover" />
              ) : (
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-200 text-sm font-bold text-gray-600">
                  {team.name.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{team.name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="font-mono text-[10px] text-gray-400">{team.id}</span>
                  {!team.parentTeamId ? (
                    <span className="rounded bg-primary-50 px-1.5 py-0 text-[10px] font-medium text-primary-600">
                      Top Level
                    </span>
                  ) : (
                    <span className="rounded bg-gray-100 px-1.5 py-0 text-[10px] font-medium text-gray-500">
                      Sub-team
                    </span>
                  )}
                  {'planName' in team && (team as { planName?: string | null }).planName && (
                    <span className="rounded bg-purple-50 px-1.5 py-0 text-[10px] font-medium text-purple-600">
                      {(team as { planName?: string | null }).planName}
                    </span>
                  )}
                  {team.slug && <span className="truncate text-[10px] text-gray-400">/{team.slug}</span>}
                </div>
              </div>
              {team.id === selectedTeamId && (
                <svg className="h-5 w-5 flex-shrink-0 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
            </div>
          )}
          {hasMore && !loading && (
            <button onClick={loadMore} className="w-full py-2 text-center text-xs text-primary-600 hover:text-primary-800">
              Load more...
            </button>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-3 space-y-2">
          {hasInternalAdminRole && (
            <button
              onClick={() => setAddAccountOpen(true)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              + Add Account
            </button>
          )}
          <button
            onClick={closeBrandSwitcher}
            className="w-full rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {addAccountOpen && (
        <AddAccountModal
          onClose={() => {
            setAddAccountOpen(false)
            closeBrandSwitcher()
          }}
        />
      )}
    </div>
  )
}
